import type { Layer } from 'effect'
import type { RuntimeDefectReporter } from './runtime-defect-reporter'
import type { TaskDeclaration, TaskProgram } from './task'

export type ProcessMessage = { type: string }

export type ProcessName = string

export type EffectOutput<Message extends ProcessMessage> =
  | Message
  | readonly Message[]
  | null
  | void

/**
 * Transition-owned work.
 *
 * An effect belongs to the update that returned it and survives later state
 * changes, but not store disposal. Its outcome must therefore land
 * deliberately. Normally the state chart keeps the owning phase present until
 * it arrives. If the product genuinely permits replacing that owner while the
 * old effect runs, the outcome carries the real owner and `update` rejects a
 * mismatch. If leaving the state should cancel the work, declare a Task instead.
 *
 * Like Elm's `Cmd`, it cannot fail: every outcome becomes a message, with
 * `Effect.match` or `sdk.call`'s handlers, or is declared a defect with
 * `Effect.orDie`.
 */
export type UpdateEffect<
  Message extends ProcessMessage,
  Error = never,
  Requirements = unknown
> = TaskProgram<EffectOutput<Message>, Error, Requirements>

export function normalizeEffectOutput<Message extends ProcessMessage>(
  output: EffectOutput<Message>
): readonly Message[] {
  if (output == null) {
    return []
  }

  if (Array.isArray(output)) {
    return output as readonly Message[]
  }

  return [output as Message]
}

/**
 * A pure state transition, optionally paired with transition-owned work.
 *
 * The runtime commits `State` before starting the Command, so any emitted
 * Message observes the new state. Commands are not part of the transaction and
 * do not roll the state back if they fail.
 */
export type UpdateResult<
  State,
  Message extends ProcessMessage,
  Error = never,
  Requirements = unknown
> = State | readonly [State, UpdateEffect<Message, Error, Requirements>]

export type UpdateStep<State, Message extends ProcessMessage> = (
  state: State
) => UpdateResult<State, Message>

declare const outputs: unique symbol

const output: unique symbol = Symbol('output')

/**
 * A transition that also hands its parent one declared output, Elm's OutMsg.
 * The output goes straight to the parent's handler in the same update; it is
 * never a message and never queued.
 */
export type Emitted<State, Message extends ProcessMessage, Output extends ProcessMessage> = {
  readonly result: UpdateResult<State, Message>
  readonly [output]: Output
}

/** What a module's `update` returns: a transition, or one with an output. */
export type ModuleUpdate<State, Message extends ProcessMessage, Output extends ProcessMessage> =
  | UpdateResult<State, Message>
  | ([Output] extends [never] ? never : Emitted<State, Message, Output>)

export function emit<
  State,
  const Output extends ProcessMessage,
  Message extends ProcessMessage = never
>(result: UpdateResult<State, Message>, value: Output): Emitted<State, Message, Output> {
  return { result, [output]: value }
}

export function isEmitted(update: unknown): update is Emitted<any, any, any> {
  return typeof update == 'object' && update != null && output in update
}

export function emittedOutput<Output extends ProcessMessage>(
  update: Emitted<unknown, ProcessMessage, Output>
): Output {
  return update[output]
}

export type ModuleSpec<
  State,
  Message extends ProcessMessage,
  InitArg = undefined,
  Declaration extends TaskDeclaration<Message, never, any, any> = TaskDeclaration<
    Message,
    never,
    any,
    any
  >,
  Output extends ProcessMessage = never
> = {
  init: [InitArg] extends [undefined]
    ? (arg?: InitArg) => UpdateResult<State, Message>
    : (arg: InitArg) => UpdateResult<State, Message>
  /** A declared output is returned with `emit`; the embedding parent handles it. */
  update: (state: State, message: Message) => ModuleUpdate<State, Message, NoInfer<Output>>
  /**
   * State-owned declarations, reconciled after each committed message.
   * Returning the same slot and arguments retains a running or terminal session.
   */
  tasks?: (state: State) => ReadonlyArray<Declaration>
  layer?: Layer.Layer<never, any, RuntimeDefectReporter>
  /** Type-only: the declared outputs, so composition infers them from here alone. */
  readonly [outputs]?: [Output]
}

export type Module<
  State,
  Message extends ProcessMessage,
  InitArg = undefined,
  Declaration extends TaskDeclaration<Message, never, any, any> = TaskDeclaration<
    Message,
    never,
    any,
    any
  >,
  Output extends ProcessMessage = never
> = ModuleSpec<State, Message, InitArg, Declaration, Output>

/** The types that define a module, named rather than positional. */
export type ModuleTypes = {
  state: unknown
  message: ProcessMessage
  /** The argument `init` takes; omit for none. */
  init?: unknown
  /** The outputs `update` may `emit` to an embedding parent; omit for none. */
  output?: ProcessMessage
}

type InitOf<T extends ModuleTypes> = T extends { init: infer InitArg } ? InitArg : undefined
type OutputOf<T extends ModuleTypes> = T extends { output: infer Output extends ProcessMessage }
  ? Output
  : never

/**
 * Defines the type-safe boundary between a module and the runtime.
 *
 * `ModuleSpec` is the compile-time validation boundary. The constructor
 * deliberately performs no runtime normalization: module authors and the
 * runtime see the same canonical shape.
 */
export function defModule<T extends ModuleTypes>(
  spec: ModuleSpec<
    T['state'],
    T['message'],
    InitOf<T>,
    TaskDeclaration<T['message'], never, any, any>,
    OutputOf<T>
  >
): Module<
  T['state'],
  T['message'],
  InitOf<T>,
  TaskDeclaration<T['message'], never, any, any>,
  OutputOf<T>
> {
  return spec
}

export type AnyModule = Module<any, any, any, TaskDeclaration<any, never, any, any>, any>

export type StateOf<M> = M extends Module<infer State, any, any, any> ? State : never
export type MessageOf<M> = M extends Module<any, infer Message, any, any> ? Message : never
export type InitArgOf<M> = M extends Module<any, any, infer InitArg, any> ? InitArg : never

export type ProcessMap = Record<string, Module<any, any, any, any>>

export type AppState<P extends ProcessMap> = {
  [K in keyof P]: StateOf<P[K]>
}

export function defineProcesses<const P extends ProcessMap>(processes: P): P {
  return processes
}

export type ProcessSend<Message extends ProcessMessage> = (message: Message | Message[]) => void

/**
 * View-facing capability bound to one registered process instance.
 *
 * The runtime revokes `send` when that instance is removed or replaced, so an
 * event from an old view cannot reach a later instance with the same name.
 */
export type ProcessHandle<Message extends ProcessMessage> = Readonly<{
  send: ProcessSend<Message>
}>
